import { handleCreditError } from './creditErrorHandler';
import {
  CONNECTION_ERROR_MESSAGE,
  isAbortError,
  isFailedFetchError,
  toSafeError,
} from '@/utils/networkError';

interface RequestOptions extends RequestInit {
  skipErrorHandling?: boolean;
  // Abort the entire request, including any GET retries, after this many milliseconds.
  timeoutMs?: number;
}

const GET_RETRY_COUNT = 2;
const GET_RETRY_BASE_DELAY_MS = 400;
const GET_RETRY_MAX_DELAY_MS = 5_000;
const RETRYABLE_GET_STATUSES = new Set([408, 502, 503, 504]);

function createAbortError(): Error {
  const error = new Error('Request aborted.');
  error.name = 'AbortError';
  return error;
}

function retryAfterMs(response: Response): number | null {
  const value = response.headers.get('Retry-After')?.trim();
  if (!value) return null;

  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(seconds * 1_000, GET_RETRY_MAX_DELAY_MS);
  }

  const date = Date.parse(value);
  if (!Number.isNaN(date)) {
    return Math.min(Math.max(0, date - Date.now()), GET_RETRY_MAX_DELAY_MS);
  }

  return null;
}

function retryDelayMs(retryIndex: number, response?: Response): number {
  const serverDelay = response ? retryAfterMs(response) : null;
  if (serverDelay !== null) return serverDelay;

  const exponentialDelay = GET_RETRY_BASE_DELAY_MS * 2 ** retryIndex;
  const jitter = Math.random() * exponentialDelay;
  return Math.min(exponentialDelay + jitter, GET_RETRY_MAX_DELAY_MS);
}

function waitForRetry(delayMs: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(createAbortError());

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', handleAbort);
      reject(createAbortError());
    };
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    }, delayMs);

    signal?.addEventListener('abort', handleAbort, { once: true });
  });
}

async function handlePaymentRequired(response: Response): Promise<never> {
  try {
    const error = await response.json();
    handleCreditError(error.detail || error.message || 'Payment required');
  } catch {
    handleCreditError('Payment required');
  }
  throw Object.assign(new Error('Payment Required'), { status: response.status });
}

export const apiClient = {
  async request(url: string, options: RequestOptions = {}) {
    const { skipErrorHandling, timeoutMs, ...fetchOptions } = options;
    const method = (fetchOptions.method || 'GET').toUpperCase();
    const maxRetries = method === 'GET' ? GET_RETRY_COUNT : 0;

    const timeoutController = timeoutMs ? new AbortController() : null;
    const callerSignal = fetchOptions.signal as AbortSignal | undefined;
    const combineSignals = (
      AbortSignal as typeof AbortSignal & {
        any?: (signals: AbortSignal[]) => AbortSignal;
      }
    ).any;
    let signalToUse = callerSignal;
    let removeCallerAbortListener: (() => void) | undefined;

    if (timeoutController) {
      if (callerSignal && combineSignals) {
        signalToUse = combineSignals([
          callerSignal,
          timeoutController.signal,
        ]);
      } else if (callerSignal) {
        const forwardCallerAbort = () => timeoutController.abort();
        if (callerSignal.aborted) {
          timeoutController.abort();
        } else {
          callerSignal.addEventListener('abort', forwardCallerAbort, {
            once: true,
          });
          removeCallerAbortListener = () =>
            callerSignal.removeEventListener('abort', forwardCallerAbort);
        }
        signalToUse = timeoutController.signal;
      } else {
        signalToUse = timeoutController.signal;
      }
    }

    const timeoutId = timeoutMs
      ? setTimeout(() => timeoutController?.abort(), timeoutMs)
      : undefined;

    try {
      for (let attempt = 0; ; attempt += 1) {
        let response: Response;
        try {
          response = await fetch(url, { ...fetchOptions, signal: signalToUse });
        } catch (error) {
          if (isAbortError(error) || signalToUse?.aborted) {
            throw toSafeError(error);
          }
          if (!isFailedFetchError(error)) {
            throw toSafeError(error);
          }
          if (attempt < maxRetries) {
            await waitForRetry(retryDelayMs(attempt), signalToUse);
            continue;
          }
          throw toSafeError(error, CONNECTION_ERROR_MESSAGE);
        }

        if (
          attempt < maxRetries &&
          RETRYABLE_GET_STATUSES.has(response.status)
        ) {
          await waitForRetry(retryDelayMs(attempt, response), signalToUse);
          continue;
        }

        if (response.status === 402 && !skipErrorHandling) {
          return await handlePaymentRequired(response);
        }

        if (response.status === 403 && !skipErrorHandling) {
          let detail: { code?: string; message?: string } | undefined;
          try {
            const error = await response.clone().json();
            if (typeof error.detail === 'object') detail = error.detail;
          } catch { /* Keep ordinary/non-JSON forbidden responses intact. */ }
          if (typeof detail?.code === 'string' && detail.code.startsWith('gcp_marketplace_')) {
            handleCreditError(detail);
            throw new Error(detail.message || 'Google Cloud Marketplace access is unavailable.');
          }
        }

        return response;
      }
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      removeCallerAbortListener?.();
    }
  },

  // Convenience methods
  get: (url: string, options?: RequestOptions) =>
    apiClient.request(url, { ...options, method: 'GET' }),

  post: (url: string, options?: RequestOptions) =>
    apiClient.request(url, { ...options, method: 'POST' }),

  put: (url: string, options?: RequestOptions) =>
    apiClient.request(url, { ...options, method: 'PUT' }),

  patch: (url: string, options?: RequestOptions) =>
    apiClient.request(url, { ...options, method: 'PATCH' }),

  delete: (url: string, options?: RequestOptions) =>
    apiClient.request(url, { ...options, method: 'DELETE' }),
};
